/* eslint-disable max-len */
/**
 * Provides current traffic incidents in a given region, on a given zoom level with a given style using the TomTom
 * [Traffic API - Traffic Incidents Details](TRAFFIC_INCIDENT_DETAILS_URL)
 *
 * The current road situation is updated every minute. Please use the
 * {{#crossLink "IncidentViewport"}}{{/crossLink}} service in conjunction with your request to get traffic
 * incidents aligned with other services.
 *
 * Parameters need to be passed to the constructor.
 * The method {{#crossLink "Services.services.incidentDetails/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * The final response from this service is a GeoJSON object.
 *
 * @class incidentDetails
 * @namespace Services.services
 * @uses GoMixin
 * @module Services
 * @uses KeyMixin
 * @uses LanguageMixin
 * @uses BoundingBoxMixin
 * @uses ProtocolMixin
 * @constructor
 * @param {Object} [options]
 *
 * @example
 * ```javascript
 * function callbackFn(response) {
 *   console.log(response);
 * }
 * tt.services.incidentDetails({
 *   key: '<Your API key>',
 *   boundingBox: '0,0,1,1',
 *   style: 's1',
 *   zoomLevel: 12
 * })
 * .go()
 * .then(callbackFn);
 * ```
 */
/* eslint-enable max-len */
/* jshint nomen:false */
import {SERVICE_TYPES} from 'Core/serviceTypes';
import incidentDetailsModel from 'model/traffic/incidentDetails';
import validators from './validators';
import converters from './converters';
import { incidentDetailsRest } from 'rest/incidentDetails';
import parameterApplications from '../common/parameterApplications';
import {serviceMethods} from './serviceFactory';
import constants from '../../core/constants';

const fields = {
    key: {
        validators: [validators.key]
    },

    /**
     * The zoom level of a currently displayed map.
     * It is needed as the style (visibility, clustering, etc...) depends on the current zoom level.
     * @attribute zoomLevel
     * @param {Number} options.zoomLevel Zoom level from `0` to `22`
     */
    zoomLevel: {
        validators: [validators.zoomLevel],
        required: true,
        application: parameterApplications.PATH,
        name: 'zoom'
    },

    boundingBox: {
        converters: [converters.boundingBox],
        validators: [validators.boundingBox],
        required: true,
        application: parameterApplications.PATH,
        cast: (boundingBox, requestOptions) => {
            requestOptions.minLon = boundingBox.minLon;
            requestOptions.maxLon = boundingBox.maxLon;
            requestOptions.minLat = boundingBox.minLat;
            requestOptions.maxLat = boundingBox.maxLat;
        }
    },

    /**
     * The style that will be used to render the
     * traffic tile in the Maps API. This will have an effect on the coordinates of traffic incidents in the
     * reply.
     *
     * @attribute style
     * @param {String} options.style Style name, needs to be one of _'s1', 's2', 's3', 'night'_. This needs to match
     *     the style of your raster traffic tiles to match traffic tubes with traffic icons.
     */
    style: {
        validators: [validators.oneOfValue(['s0', 's0-dark', 's1', 's2', 's3', 'night'], 'traffic style')],
        required: true,
        application: parameterApplications.PATH
    },

    language: {
        converters: [converters.incidentDetailsLanguage],
        validators: [validators.incidentDetailsLanguage]
    },

    /**
     * Number referencing the traffic model to use.
     * This can be obtained from the {{#crossLink "IncidentViewport"}}{{/crossLink}} service.
     *
     * It is updated every minute, and is valid for two minutes before it times out.
     *
     * If a wrong Traffic Model ID is specified, the correct one will be returned by the interface.
     *
     * The default value of `-1` will always invoke the most recent traffic model. Nevertheless, it is
     * good practice to use the value obtained from the service as this will guarantee an alignment
     * with the raster traffic layer.
     *
     * @attribute trafficModelID
     * @param {Number} [options.trafficModelID] A valid, not older than two minutes, traffic model ID.
     */
    trafficModelID: {
        validators: [validators.string],
        defaultValue: constants.VIEWPORT_DEFAULT_VALUE,
        application: parameterApplications.PATH
    },

    geometries: {
        validators: [validators.oneOfValue(['shifted', 'original'], 'traffic geometries')]
    },

    /**
     * Tells the service to separately list all traffic incidents in a cluster.
     *
     * When is enabled the clusters in the response will be represented as geometry collections,
     * and the cluster coordinates will appear as clusterCoordinates parameters.
     *
     * Enabling this feature is useful when the user wants to obtain details of the incidents
     * that are being clustered, or wants to perform a client-side clustering.
     *
     * Note that when this is set to _false_, the _toGeoJson()_ method available
     * in the resolved response will return only unclustered POIs, but **no clusters**.
     *
     * @attribute expandCluster
     * @param {Boolean} [options.expandCluster=false] Determines if the service should include details of incidents
     * that are clustered.
     */
    expandCluster: {
        validators: [validators.bool],
        defaultValue: false
    },

    /**
     * If this is set to true, the _toGeoJson()_ method available
     * in the resolved response will return features with an additional `properties.features`
     * field containing clustered features.
     *
     * Note that this option only works in conjunction with `expandCluster` set to `true`.
     *
     * @attribute preserveCluster
     * @param {Boolean} [options.preserveCluster=false] Determines if clustered incidents data
     * should be available in the cluster feature.
     */
    preserveCluster: {
        validators: [validators.bool],
        defaultValue: false
    },

    /**
     * Return the original position of the incident (`originalPosition` attribute) as well as the one
     * shifted to the beginning of the traffic tube.
     *
     * @attribute originalPosition
     * @param {Boolean} [options.originalPosition=false] Determines if the service
     * should include the original incidents' position.
     */
    originalPosition: {
        validators: [validators.bool],
        defaultValue: false
    },

    protocol: {
        validators: [validators.oneOfValue(['http', 'https'], 'protocol')]
    },

    projection: {
        defaultValue: 'EPSG4326'
    }
};

function buildHandleChain(preserveCluster) {
    const model = incidentDetailsModel(preserveCluster);
    return function(requestOptions) {
        return incidentDetailsRest(fields, requestOptions)
            .then(response => model(response));
    };
}

export const incidentDetails = (options) => {
    return serviceMethods(
        fields,
        options,
        SERVICE_TYPES.TRAFFIC_INCIDENTS,
        'incidentDetails',
        buildHandleChain(options && options.preserveCluster)
    );
};



// WEBPACK FOOTER //
// ./services/services/incidentDetails.js